import { CSSProperties, memo, useEffect, useState } from 'react';
import { Channel } from '../core/project/types';
import { useAppStore } from '../state/store';
import { VSlider } from './widgets/VSlider';

/**
 * One channel strip in the rack. Actions are read from the store directly
 * (stable across renders) so the row can be memoized — dragging one fader
 * must not re-render every other row.
 */
const ChannelRow = memo(function ChannelRow({
  channel: ch,
  selected,
}: {
  channel: Channel;
  selected: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(ch.name);
  useEffect(() => setName(ch.name), [ch.name]);

  const patch = (p: Partial<Channel>) =>
    useAppStore.getState().updateChannel(ch.id, p);

  return (
    <div
      className={`rack-row ${selected ? 'selected' : ''}`}
      style={{ '--ch-color': ch.color } as CSSProperties}
      onClick={() => useAppStore.getState().selectChannel(ch.id)}
    >
      <div className="rack-color" />
      {editing ? (
        <input
          className="rack-name-input"
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          onBlur={() => {
            setEditing(false);
            if (name.trim()) patch({ name: name.trim() });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setName(ch.name);
              setEditing(false);
            }
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span className="rack-name" onDoubleClick={() => setEditing(true)} title="Double-click to rename">
          {ch.name}
        </span>
      )}
      <button
        className={`ms-btn muted ${ch.muted ? 'on' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          patch({ muted: !ch.muted });
        }}
        title="Mute"
      >
        M
      </button>
      <button
        className={`ms-btn solo ${ch.solo ? 'on' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          patch({ solo: !ch.solo });
        }}
        title="Solo"
      >
        S
      </button>
      <VSlider
        className="rack-fader"
        value={ch.volume / 1.5}
        onChange={(v) => patch({ volume: v * 1.5 })}
      />
      <button
        className="rack-remove"
        onClick={(e) => {
          e.stopPropagation();
          useAppStore.getState().removeChannel(ch.id);
        }}
        title="Remove channel"
      >
        ×
      </button>
    </div>
  );
});

export function ChannelRack() {
  const channels = useAppStore((s) => s.project.channels);
  const selectedId = useAppStore((s) => s.selectedChannelId);
  const { addChannel } = useAppStore.getState();

  return (
    <div className="rack">
      <div className="rack-add">
        <button onClick={() => addChannel('synth')}>+ Synth</button>
        <button onClick={() => addChannel('drum')}>+ Drum</button>
        <button onClick={() => addChannel('sampler')}>+ Sampler</button>
      </div>
      <div className="rack-list">
        {channels.map((ch) => (
          <ChannelRow key={ch.id} channel={ch} selected={ch.id === selectedId} />
        ))}
        {channels.length === 0 && (
          <div className="placeholder">No channels yet. Add a synth or drum channel above.</div>
        )}
      </div>
    </div>
  );
}